import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { liveVisitorsApi, type LiveVisitor, type LiveEvent } from '../../api/endpoints/liveVisitors';
import { analyticsApi } from '../../api/endpoints/analytics';
import { useStore } from '../../contexts/StoreContext';

/* ── ISO numeric (topojson country id) → 2-letter code ── */
const ISO_MAP: Record<string, string> = {
  '682':'SA','784':'AE','414':'KW','48':'BH','634':'QA','512':'OM',
  '818':'EG','400':'JO','422':'LB','368':'IQ','760':'SY','887':'YE',
  '729':'SD','504':'MA','12':'DZ','788':'TN','434':'LY','792':'TR',
  '364':'IR','840':'US','826':'GB','250':'FR','276':'DE','724':'ES',
  '380':'IT','356':'IN','586':'PK','156':'CN','392':'JP','76':'BR',
  '484':'MX','36':'AU','643':'RU','804':'UA','410':'KR','275':'PS',
  '706':'SO','566':'NG','710':'ZA','458':'MY','360':'ID','124':'CA',
};

/* ── country labels (Arabic) ── */
const COUNTRY_LABELS = [
  { lat:23.8,lng:45.1,name:'السعودية',size:1.2 },{ lat:23.5,lng:54.5,name:'الإمارات',size:0.8 },
  { lat:29.3,lng:47.5,name:'الكويت',size:0.7 },{ lat:26.1,lng:50.6,name:'البحرين',size:0.6 },
  { lat:25.3,lng:51.2,name:'قطر',size:0.6 },{ lat:21.0,lng:57.0,name:'عُمان',size:0.8 },
  { lat:27.0,lng:30.0,name:'مصر',size:1.0 },{ lat:31.2,lng:36.8,name:'الأردن',size:0.7 },
  { lat:33.9,lng:35.8,name:'لبنان',size:0.6 },{ lat:33.0,lng:43.5,name:'العراق',size:0.9 },
  { lat:35.0,lng:38.5,name:'سوريا',size:0.7 },{ lat:15.5,lng:47.5,name:'اليمن',size:0.8 },
  { lat:16.0,lng:30.0,name:'السودان',size:0.8 },{ lat:32.0,lng:-6.0,name:'المغرب',size:0.8 },
  { lat:28.0,lng:3.0,name:'الجزائر',size:0.9 },{ lat:34.0,lng:9.0,name:'تونس',size:0.7 },
  { lat:27.0,lng:18.0,name:'ليبيا',size:0.8 },{ lat:31.9,lng:35.2,name:'فلسطين',size:0.5 },
  { lat:5.0,lng:46.0,name:'الصومال',size:0.7 },
  { lat:39.0,lng:35.0,name:'تركيا',size:0.9 },{ lat:32.5,lng:53.5,name:'إيران',size:0.9 },
  { lat:54.0,lng:-2.0,name:'بريطانيا',size:0.8 },{ lat:46.5,lng:2.5,name:'فرنسا',size:0.8 },
  { lat:51.0,lng:10.5,name:'ألمانيا',size:0.8 },{ lat:40.0,lng:-4.0,name:'إسبانيا',size:0.8 },
  { lat:42.5,lng:12.5,name:'إيطاليا',size:0.8 },{ lat:61.0,lng:100.0,name:'روسيا',size:1.2 },
  { lat:36.5,lng:128.0,name:'كوريا',size:0.7 },{ lat:36.0,lng:138.0,name:'اليابان',size:0.8 },
  { lat:35.0,lng:105.0,name:'الصين',size:1.0 },{ lat:22.0,lng:80.0,name:'الهند',size:1.0 },
  { lat:30.5,lng:69.5,name:'باكستان',size:0.9 },{ lat:4.0,lng:109.5,name:'ماليزيا',size:0.7 },
  { lat:-2.0,lng:118.0,name:'إندونيسيا',size:0.9 },{ lat:9.0,lng:8.0,name:'نيجيريا',size:0.9 },
  { lat:-29.0,lng:25.0,name:'جنوب أفريقيا',size:0.8 },
  { lat:39.0,lng:-98.0,name:'أمريكا',size:1.0 },{ lat:60.0,lng:-96.0,name:'كندا',size:0.9 },
  { lat:-10.0,lng:-52.0,name:'البرازيل',size:1.0 },{ lat:23.5,lng:-102.0,name:'المكسيك',size:0.9 },
  { lat:-25.0,lng:134.0,name:'أستراليا',size:0.9 },
];

const SAUDI_REGIONS = [
  { lat:24.7,lng:46.7,name:'الرياض',size:0.7 },{ lat:21.4,lng:39.8,name:'مكة المكرمة',size:0.7 },
  { lat:24.5,lng:39.6,name:'المدينة المنورة',size:0.6 },{ lat:26.4,lng:50.0,name:'المنطقة الشرقية',size:0.7 },
  { lat:18.2,lng:42.5,name:'عسير',size:0.6 },{ lat:28.4,lng:36.6,name:'تبوك',size:0.6 },
  { lat:27.5,lng:41.7,name:'حائل',size:0.6 },{ lat:26.3,lng:43.9,name:'القصيم',size:0.6 },
];

const COUNTRY_NAMES: Record<string, string> = {
  SA:'السعودية',AE:'الإمارات',KW:'الكويت',BH:'البحرين',QA:'قطر',OM:'عُمان',
  EG:'مصر',JO:'الأردن',LB:'لبنان',IQ:'العراق',SY:'سوريا',PS:'فلسطين',
  YE:'اليمن',SD:'السودان',LY:'ليبيا',TN:'تونس',DZ:'الجزائر',MA:'المغرب',
  US:'أمريكا',GB:'بريطانيا',DE:'ألمانيا',FR:'فرنسا',TR:'تركيا',IN:'الهند',
  PK:'باكستان',BD:'بنغلاديش',MY:'ماليزيا',ID:'إندونيسيا',CN:'الصين',JP:'اليابان',
};

const EVENT_LABELS: Record<string, string> = {
  page_view: 'يتصفح المتجر',
  product_view: 'يشاهد منتج',
  add_to_cart: 'أضاف للسلة',
  checkout_start: 'بدأ الشراء',
};

const CITY_AR: Record<string, string> = {
  Riyadh:'الرياض',Jeddah:'جدة',Mecca:'مكة',Medina:'المدينة',Dammam:'الدمام',
  Khobar:'الخبر',Tabuk:'تبوك',Abha:'أبها',Taif:'الطائف',
  Dubai:'دبي','Abu Dhabi':'أبوظبي',Sharjah:'الشارقة',
  'Kuwait City':'الكويت',Manama:'المنامة',Doha:'الدوحة',Muscat:'مسقط',
  Cairo:'القاهرة',Amman:'عمّان',Beirut:'بيروت',Baghdad:'بغداد',
  Istanbul:'إسطنبول',London:'لندن',Paris:'باريس',
  'New York':'نيويورك',Tokyo:'طوكيو',
};

function esc(s: string) { return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

export function ImmersiveGlobe() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { currentStore } = useStore();

  const containerRef = useRef<HTMLDivElement>(null);
  const labelLayerRef = useRef<HTMLDivElement>(null);
  const feedRef = useRef<HTMLDivElement>(null);
  const globeRef = useRef<any>(null);
  const labelDivsRef = useRef<HTMLDivElement[]>([]);
  const activeLabelsRef = useRef<any[]>([]);
  const arcsDataRef = useRef<any[]>([]);
  const visitorCitiesRef = useRef<any[]>([]);
  const countriesRef = useRef(new Set<string>());
  const merchantGeoRef = useRef<{ lat: number; lng: number } | null>(null);
  const hoveredPolyRef = useRef<any>(null);
  const [countryStatsPopup, setCountryStatsPopup] = useState<
    { code: string; name: string; x: number; y: number; loading: boolean; stats?: { visits: number; orders: number; revenue: number; activeNow: number } } | null
  >(null);

  const [activeCount, setActiveCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [countryCount, setCountryCount] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const addToFeed = useCallback((visitor: any) => {
    const feed = feedRef.current;
    if (!feed) return;
    const label = EVENT_LABELS[visitor.event];
    if (!label) return;

    const card = document.createElement('div');
    card.className = 'feed-card';
    let dotCls = 'feed-dot';
    if (visitor.event === 'product_view') dotCls += ' product';
    else if (visitor.event === 'add_to_cart') dotCls += ' cart';

    const country = visitor.geo?.country ? (COUNTRY_NAMES[visitor.geo.country] || visitor.geo.country) : '';
    const title = visitor.title ? ' - ' + esc(visitor.title) : '';

    card.innerHTML = `<div class="${dotCls}"></div>
      <div class="feed-text">
        <div class="feed-main">${esc(label)}${title}</div>
        <div class="feed-sub">${esc(country)}</div>
      </div>`;

    feed.insertBefore(card, feed.firstChild);
    while (feed.children.length > 12) {
      const last = feed.lastChild as HTMLElement;
      if (last) { last.classList.add('out'); setTimeout(() => last.remove(), 300); }
    }
    setTotalPages((p) => p + 1);
  }, []);

  // Globe initialization
  useEffect(() => {
    if (!storeId || !containerRef.current) return;

    let cancelled = false;
    let rafId = 0;

    const initGlobe = () => {
      const Globe = (window as any).Globe;
      if (!Globe || !containerRef.current || cancelled) return;

      // Clear any previous globe DOM
      containerRef.current.innerHTML = '';
      if (labelLayerRef.current) labelLayerRef.current.innerHTML = '';
      labelDivsRef.current = [];
      activeLabelsRef.current = [];

      const globe = Globe()(containerRef.current)
        .backgroundColor('rgba(0,0,0,0)')
        .showAtmosphere(true)
        .atmosphereColor('rgba(60,80,140,0.2)')
        .atmosphereAltitude(0.15)
        .showGlobe(true)
        .globeImageUrl('')
        .labelsData([])
        .htmlElementsData([])
        .htmlLat((d: any) => d.lat)
        .htmlLng((d: any) => d.lng)
        .htmlAltitude(0.02)
        .htmlElement((d: any) => {
          // Kept only for optional badge overlay - main marker is 3D pointsData below
          const el = document.createElement('div');
          if (d.count > 1) {
            el.textContent = String(d.count);
            el.style.cssText = 'background:#10b981;color:#fff;font-size:10px;font-weight:800;min-width:18px;height:18px;border-radius:9px;display:flex;align-items:center;justify-content:center;padding:0 4px;font-family:Tajawal;border:2px solid #0a0e1e;pointer-events:none';
          } else {
            el.style.display = 'none';
          }
          return el;
        })
        .pointsData([])
        .pointLat((d: any) => d.lat)
        .pointLng((d: any) => d.lng)
        .pointColor(() => '#10ffa0')
        .pointAltitude(0.015)
        .pointRadius((d: any) => Math.min(0.3, 0.12 + (d.count || 1) * 0.03))
        .ringsData([])
        .ringLat((d: any) => d.lat)
        .ringLng((d: any) => d.lng)
        .ringColor(() => (t: number) => `rgba(16,255,160,${1 - t})`)
        .ringMaxRadius(1.5)
        .ringPropagationSpeed(1.2)
        .ringRepeatPeriod(1400)
        .ringAltitude(0.012)
        .arcsData([])
        .arcStartLat((d: any) => d.startLat)
        .arcStartLng((d: any) => d.startLng)
        .arcEndLat((d: any) => d.endLat)
        .arcEndLng((d: any) => d.endLng)
        .arcColor((d: any) => d.color)
        .arcAltitude(0.35)
        .arcStroke(2.2)
        .arcDashLength(0.6)
        .arcDashGap(0.15)
        .arcDashAnimateTime(2000);

      // Style the globe material
      try {
        const mat = globe.globeMaterial();
        mat.color.set(0x080c1a);
        mat.emissive.set(0x060810);
        mat.emissiveIntensity = 0.4;
        globe.renderer().setClearColor(0x04060e, 1);
      } catch {}

      globe.pointOfView({ lat: 24.5, lng: 46, altitude: 2.2 }, 0);
      globe.controls().autoRotate = false;
      globe.controls().enableZoom = true;

      globeRef.current = globe;

      // Load polygons (topojson may already be loaded)
      const loadPolygons = () => {
        if (cancelled) return;
        fetch('https://unpkg.com/world-atlas@2/countries-110m.json')
          .then((r) => r.json())
          .then((topology) => {
            if (cancelled) return;
            const topojson = (window as any).topojson;
            const features = topojson.feature(topology, topology.objects.countries).features;

            const applyPolygons = () => {
              globe
                .polygonsData(features)
                .polygonLabel(() => false as any)
                .polygonCapCurvatureResolution(1)
                .polygonCapColor((d: any) => (d === hoveredPolyRef.current ? 'rgba(59,130,246,0.12)' : 'rgba(0,0,0,0)'))
                .polygonSideColor(() => 'rgba(0,0,0,0)')
                .polygonStrokeColor((d: any) => {
                  if (d.id === '682') return 'rgba(59,130,246,0.7)';
                  if (d === hoveredPolyRef.current) return 'rgba(59,130,246,0.5)';
                  return 'rgba(255,255,255,0.06)';
                })
                .polygonAltitude((d: any) => d.id === '682' ? 0.003 : 0.001)
                .onPolygonHover((poly: any) => {
                  hoveredPolyRef.current = poly;
                  applyPolygons(); // re-apply to refresh colors
                  if (containerRef.current) {
                    containerRef.current.style.cursor = poly ? 'pointer' : 'default';
                  }
                })
                .onPolygonClick((poly: any, event: any) => {
                  if (!poly || !poly.id) return;
                  const code = ISO_MAP[String(poly.id)] || poly.id;
                  const name = poly.properties?.name || code;
                  setCountryStatsPopup({ code, name, x: event.clientX, y: event.clientY, loading: true });
                });
            };

            applyPolygons();
          })
          .catch(() => {});
      };

      if ((window as any).topojson) {
        loadPolygons();
      } else {
        const topoScript = document.createElement('script');
        topoScript.src = 'https://unpkg.com/topojson-client@3/dist/topojson-client.min.js';
        topoScript.onload = loadPolygons;
        document.head.appendChild(topoScript);
      }

      // Label system
      if (!cancelled) buildAndSyncLabels(globe);

      globe.controls().addEventListener('change', () => {
        if (cancelled) return;
        clearTimeout((globe as any)._lt);
        (globe as any)._lt = setTimeout(() => {
          if (!cancelled) buildAndSyncLabels(globe);
        }, 100);
      });

      // Label positioning loop
      let frame = 0;
      const tickLabels = () => {
        if (cancelled) return;
        rafId = requestAnimationFrame(tickLabels);
        frame++;
        if (frame % 2 !== 0) return;
        try {
          const pov = globe.pointOfView();
          const divs = labelDivsRef.current;
          const labels = activeLabelsRef.current;
          for (let i = 0; i < labels.length; i++) {
            const el = divs[i];
            if (!el) continue;
            const dLat = Math.abs((el as any)._lat - pov.lat);
            let dLng = Math.abs((el as any)._lng - pov.lng);
            if (dLng > 180) dLng = 360 - dLng;
            const angDist = dLat + dLng;
            if (angDist > 120) { el.style.opacity = '0'; continue; }
            const coords = globe.getScreenCoords((el as any)._lat, (el as any)._lng, 0.005);
            if (!coords) { el.style.opacity = '0'; continue; }
            el.style.opacity = angDist > 80 ? '0.08' : angDist > 55 ? '0.2' : '1';
            el.style.left = Math.round(coords.x) + 'px';
            el.style.top = Math.round(coords.y) + 'px';
          }
        } catch {}
      };
      tickLabels();

      setTimeout(() => { if (!cancelled) buildAndSyncLabels(globe); }, 300);
      setLoaded(true);
    };

    // Load globe.gl script or use cached
    if ((window as any).Globe) {
      initGlobe();
    } else {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/globe.gl@2.45.3/dist/globe.gl.min.js';
      script.onload = () => { if (!cancelled) initGlobe(); };
      document.head.appendChild(script);
    }

    // Resize handler
    const onResize = () => {
      if (globeRef.current) {
        globeRef.current.width(window.innerWidth);
        globeRef.current.height(window.innerHeight);
      }
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
      // Destroy Three.js renderer
      if (globeRef.current) {
        try { globeRef.current.renderer().dispose(); } catch {}
        try { globeRef.current.scene().clear(); } catch {}
        globeRef.current = null;
      }
      // Clear DOM
      if (containerRef.current) containerRef.current.innerHTML = '';
      if (labelLayerRef.current) labelLayerRef.current.innerHTML = '';
      labelDivsRef.current = [];
      activeLabelsRef.current = [];
    };
  }, [storeId]);

  // SSE connection
  useEffect(() => {
    if (!storeId || !loaded) return;

    // Load initial data
    liveVisitorsApi.getActive(storeId).then((data) => {
      setActiveCount(data.activeCount || 0);
      updateMarkers(data.visitors || []);
      (data.visitors || []).forEach((v: any) => addToFeed(v));
    }).catch(() => {});

    // SSE
    const url = liveVisitorsApi.getStreamUrl(storeId);
    const es = new EventSource(url, { withCredentials: true });

    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'active_count') {
          setActiveCount(msg.count);
          if (msg.merchantGeo?.lat && msg.merchantGeo?.lng) {
            merchantGeoRef.current = { lat: msg.merchantGeo.lat, lng: msg.merchantGeo.lng };
          }
          updateMarkers(msg.visitors || []);
        } else if (msg.type === 'event') {
          if (['page_view', 'product_view', 'add_to_cart', 'checkout_start'].includes(msg.event)) {
            addToFeed(msg);
          }
        }
      } catch {}
    };

    return () => es.close();
  }, [storeId, loaded, addToFeed]);

  function buildAndSyncLabels(globe: any) {
    const pov = globe.pointOfView();
    const alt = pov.altitude;
    const nearSaudi = Math.abs(pov.lat - 24) < 15 && Math.abs(pov.lng - 45) < 20;
    const list: any[] = [];

    COUNTRY_LABELS.forEach((c) => {
      if (nearSaudi && alt < 1.5 && c.name === 'السعودية') return;
      list.push({ lat: c.lat, lng: c.lng, name: c.name, fs: (alt > 1.5 ? c.size : c.size * 0.6) * 12, bold: true });
    });

    if (nearSaudi && alt < 1.8) {
      SAUDI_REGIONS.forEach((r) => {
        list.push({ lat: r.lat, lng: r.lng, name: r.name, fs: Math.max(10, r.size * 14), bold: true });
      });
    }

    visitorCitiesRef.current.forEach((c) => {
      const name = CITY_AR[c.name] || c.name;
      const fs = Math.max(10, Math.min(16, 10 + c.count * 2));
      list.push({ lat: c.lat, lng: c.lng, name: name + (c.count > 1 ? ` (${c.count})` : ''), fs, bold: false });
    });

    syncLabelDivs(list);
  }

  function syncLabelDivs(list: any[]) {
    const layer = labelLayerRef.current;
    if (!layer) return;

    // Clear ALL old label divs completely
    while (layer.firstChild) layer.removeChild(layer.firstChild);
    const newDivs: HTMLDivElement[] = [];

    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      const el = document.createElement('div');
      el.style.cssText = 'position:absolute;font-family:Tajawal,sans-serif;white-space:nowrap;user-select:none;pointer-events:none;text-shadow:0 1px 6px rgba(0,0,0,0.9),0 0 20px rgba(0,0,0,0.6);transform:translate(-50%,-50%)';
      el.textContent = item.name;
      (el as any)._lat = item.lat;
      (el as any)._lng = item.lng;
      el.style.fontSize = Math.max(11, item.fs) + 'px';
      el.style.fontWeight = item.bold ? '700' : '500';
      el.style.color = item.bold ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.25)';
      el.style.opacity = '1';
      layer.appendChild(el);
      newDivs.push(el);
    }

    labelDivsRef.current = newDivs;
    activeLabelsRef.current = list;
  }

  function updateMarkers(visitors: LiveVisitor[]) {
    const globe = globeRef.current;
    if (!globe) return;

    const shopMarkers: any[] = [];
    const arcs: any[] = [];
    const cities: any[] = [];
    const countrySet = new Set<string>();
    const spots: Record<string, any> = {};

    visitors.forEach((v) => {
      const lng = (v.geo as any)?.lng ?? v.geo?.lon;
      if (v.geo?.lat && lng) {
        const city = v.geo.city || '';
        const key = city || `${v.geo.lat.toFixed(1)},${Number(lng).toFixed(1)}`;
        if (!spots[key]) spots[key] = { lat: v.geo.lat, lng, name: city, count: 0 };
        spots[key].count++;
        if (v.geo.country) countrySet.add(v.geo.country);
      }
    });

    Object.values(spots).forEach((s: any) => {
      shopMarkers.push({ lat: s.lat, lng: s.lng, count: s.count });
      if (s.name) cities.push({ lat: s.lat, lng: s.lng, name: s.name, count: s.count });
      const originLat = merchantGeoRef.current?.lat ?? 24.7136;
      const originLng = merchantGeoRef.current?.lng ?? 46.6753;
      // Draw arc unless coords are essentially identical (< 0.05° ≈ 5km)
      if (Math.abs(s.lat - originLat) > 0.05 || Math.abs(s.lng - originLng) > 0.05) {
        arcs.push({
          startLat: originLat, startLng: originLng,
          endLat: s.lat, endLng: s.lng,
          color: ['rgba(16,255,160,1)', 'rgba(16,255,160,0.5)'],
        });
      }
    });

    visitorCitiesRef.current = cities;
    countriesRef.current = countrySet;
    setCountryCount(countrySet.size);

    globe.htmlElementsData(shopMarkers);
    globe.pointsData(shopMarkers);
    globe.ringsData(shopMarkers);
    globe.arcsData(arcs);

    if (shopMarkers.length > 0) {
      let avgLat = 0, avgLng = 0;
      shopMarkers.forEach((p: any) => { avgLat += p.lat; avgLng += p.lng; });
      avgLat /= shopMarkers.length;
      avgLng /= shopMarkers.length;
      globe.pointOfView({ lat: avgLat, lng: avgLng, altitude: 2 }, 1500);
    }

    buildAndSyncLabels(globe);
  }

  // Fetch country stats when popup opens
  useEffect(() => {
    if (!countryStatsPopup?.code || !storeId || !countryStatsPopup.loading) return;
    let cancelled = false;
    analyticsApi.getCountryStats(storeId, countryStatsPopup.code)
      .then((res) => {
        if (cancelled) return;
        setCountryStatsPopup((prev) => prev ? { ...prev, loading: false, stats: res.stats } : prev);
      })
      .catch(() => {
        if (!cancelled) setCountryStatsPopup((prev) => prev ? { ...prev, loading: false, stats: { visits: 0, orders: 0, revenue: 0, activeNow: 0 } } : prev);
      });
    return () => { cancelled = true; };
  }, [countryStatsPopup?.code, countryStatsPopup?.loading, storeId]);

  return (
    <>
      <style>{`
        .immersive-globe * { margin: 0; padding: 0; box-sizing: border-box; }
        .immersive-globe { width: 100vw; height: 100vh; position: fixed; inset: 0; z-index: 9999; background: #04060e; font-family: 'Tajawal', sans-serif; color: #fff; overflow: hidden; }
        .immersive-globe .globe-bg { position: absolute; inset: 0; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 55%, rgba(59,130,246,0.03) 0%, transparent 50%); }
        .immersive-globe .top-bar { position: fixed; top: 0; left: 0; right: 0; z-index: 50; display: flex; align-items: center; justify-content: space-between; padding: 18px 28px; background: linear-gradient(to bottom, rgba(4,6,14,0.9) 0%, transparent 100%); }
        .immersive-globe .store-name { font-size: 15px; font-weight: 700; opacity: 0.9; }
        .immersive-globe .back-link { color: rgba(255,255,255,0.4); text-decoration: none; font-size: 13px; cursor: pointer; border: none; background: none; }
        .immersive-globe .back-link:hover { color: #fff; }
        .immersive-globe .globe-container { position: absolute; inset: 0; z-index: 1; }
        .immersive-globe .label-layer { position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 2; }
        .immersive-globe .active-box { position: fixed; bottom: 36px; right: 36px; z-index: 50; background: rgba(255,255,255,0.04); backdrop-filter: blur(20px); border: 1px solid rgba(255,255,255,0.06); border-radius: 20px; padding: 24px 32px; min-width: 160px; }
        .immersive-globe .active-box .label { font-size: 12px; opacity: 0.4; margin-bottom: 6px; }
        .immersive-globe .active-box .num { font-size: 48px; font-weight: 800; line-height: 1; letter-spacing: -2px; }
        .immersive-globe .active-box .sub { font-size: 11px; opacity: 0.25; margin-top: 6px; }
        .immersive-globe .feed { position: fixed; top: 72px; right: 28px; z-index: 50; width: 260px; max-height: calc(100vh - 200px); overflow-y: auto; display: flex; flex-direction: column; gap: 6px; scrollbar-width: none; }
        .immersive-globe .feed::-webkit-scrollbar { display: none; }
        .immersive-globe .feed-card { background: rgba(255,255,255,0.03); backdrop-filter: blur(12px); border: 1px solid rgba(255,255,255,0.05); border-radius: 14px; padding: 12px 16px; display: flex; align-items: center; gap: 10px; animation: igFeedIn 0.35s ease-out; }
        .immersive-globe .feed-card.out { opacity: 0; transform: translateX(20px); transition: all 0.3s; }
        @keyframes igFeedIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        .immersive-globe .feed-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; background: #10b981; box-shadow: 0 0 8px rgba(16,185,129,0.4); }
        .immersive-globe .feed-dot.product { background: #22d3ee; box-shadow: 0 0 8px rgba(34,211,238,0.4); }
        .immersive-globe .feed-dot.cart { background: #fbbf24; box-shadow: 0 0 8px rgba(251,191,36,0.4); }
        .immersive-globe .feed-text { flex: 1; min-width: 0; }
        .immersive-globe .feed-main { font-size: 12px; font-weight: 600; opacity: 0.85; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .immersive-globe .feed-sub { font-size: 10px; opacity: 0.3; margin-top: 2px; }
        .immersive-globe .stats-row { position: fixed; bottom: 36px; left: 36px; z-index: 50; display: flex; gap: 12px; }
        .immersive-globe .stat-pill { background: rgba(255,255,255,0.04); backdrop-filter: blur(20px); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 12px 18px; text-align: center; }
        .immersive-globe .stat-pill .val { font-size: 20px; font-weight: 700; }
        .immersive-globe .stat-pill .lbl { font-size: 10px; opacity: 0.3; margin-top: 2px; }
        @media (max-width: 768px) {
          .immersive-globe .feed { width: 180px; right: 12px; top: 60px; }
          .immersive-globe .active-box { bottom: 16px; right: 16px; padding: 16px 20px; }
          .immersive-globe .active-box .num { font-size: 36px; }
          .immersive-globe .stats-row { left: 16px; bottom: 16px; }
        }
      `}</style>
      <link rel="preload" href="https://fonts.googleapis.com/css2?family=Tajawal:wght@500;700&display=swap" as="style" onLoad={(e) => { (e.target as HTMLLinkElement).rel = 'stylesheet'; }} />
      <div className="immersive-globe" dir="rtl">
        <div className="globe-bg" />
        <div className="top-bar">
          <div className="store-name">{currentStore?.name || ''}</div>
          <button className="back-link" onClick={() => navigate(`/${storeId}/analytics/live`)}>← الزوار المباشرون</button>
        </div>
        <div className="globe-container" ref={containerRef} />
        <div className="label-layer" ref={labelLayerRef} />
        <div className="feed" ref={feedRef} />
        <div className="active-box">
          <div className="label">زوار الآن</div>
          <div className="num">{activeCount}</div>
          <div className="sub">مباشر</div>
        </div>
        <div className="stats-row">
          <div className="stat-pill"><div className="val">{totalPages}</div><div className="lbl">صفحات</div></div>
          <div className="stat-pill"><div className="val">{countryCount}</div><div className="lbl">دول</div></div>
        </div>

        {/* Country stats popup */}
        {countryStatsPopup && (
          <div
            style={{
              position: 'fixed',
              left: Math.min(countryStatsPopup.x, window.innerWidth - 260),
              top: Math.min(countryStatsPopup.y, window.innerHeight - 220),
              zIndex: 60,
              background: 'rgba(10,14,30,0.92)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 14,
              padding: '18px 20px',
              minWidth: 240,
              color: '#fff',
              fontFamily: 'Tajawal, sans-serif',
              boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
            }}
          >
            <button
              onClick={() => setCountryStatsPopup(null)}
              style={{ position: 'absolute', top: 8, left: 10, background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 14, cursor: 'pointer' }}
            >✕</button>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>{countryStatsPopup.name}</div>
            {countryStatsPopup.loading ? (
              <div style={{ fontSize: 12, opacity: 0.5 }}>جاري التحميل...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ opacity: 0.55 }}>نشط الآن</span>
                  <span style={{ fontWeight: 700, color: '#10ffa0' }}>{countryStatsPopup.stats?.activeNow ?? 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ opacity: 0.55 }}>الزيارات (30 يوم)</span>
                  <span style={{ fontWeight: 700 }}>{countryStatsPopup.stats?.visits ?? 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ opacity: 0.55 }}>الطلبات</span>
                  <span style={{ fontWeight: 700 }}>{countryStatsPopup.stats?.orders ?? 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ opacity: 0.55 }}>الإيرادات</span>
                  <span style={{ fontWeight: 700 }}>{countryStatsPopup.stats?.revenue ?? 0} SAR</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
